import { useEffect, useState } from "react";

import { PaymentMethod } from "common/components/PaymentMethod";
import RenderIf from "common/components/RenderIf";
import AgencySubscription from "common/components/subscriptions/AgencySubscription";
import PricingPeriodInfoSelector from "common/components/subscriptions/PricingPeriodInfoSelector";
import RentalSubscription from "common/components/subscriptions/RentalSubscription";
import SubscriptionPlan from "common/components/subscriptions/SubscriptionPlan";
import Success from "common/components/subscriptions/Success";
import Summary from "common/components/subscriptions/Summary";
import { Button } from "common/components/ui/Button";
import { DialogFooter, DialogTitle } from "common/components/ui/Dialog";
import { DrawerFooter, DrawerTitle } from "common/components/ui/Drawer";
import type { PRICING_PLANS } from "common/constants";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import usePaymentSource from "common/datahooks/usePaymentSource";
import usePricing from "common/datahooks/usePricing";
import usePromotions from "common/datahooks/usePromotions";
import useWorkspaceDetails from "common/datahooks/useWorkspaceDetails";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";
import type {
  AgencySubscription as AgencySubscriptionType,
  Pricing,
  PricingPeriodInfo,
  RentedSubscription,
  Subscription,
} from "common/types";

const stepLabels = {
  manage: "Manage Subscription",
  cancelAccounts: "Remove seats",
  payment: "",
  summary: "Summary",
  success: "",
} as const;

export default function ManageSubscription({
  plan,
  fixedPricingPeriodInfo,
  minQuantity,
  onClose,
}: {
  plan: keyof typeof PRICING_PLANS;
  //if this is provided, this period is used, and pricing period selection is not available
  fixedPricingPeriodInfo?: PricingPeriodInfo;
  minQuantity?: number;
  onClose: () => void;
}) {
  const isDesktop = useTwBreakpoint("lg");
  const { id: workspaceId } = useSelectedWorkspaceContext();
  const { workspace } = useWorkspaceDetails(workspaceId);
  const { agencyPlan } = usePromotions();

  let currentSubscription:
    | RentedSubscription
    | Subscription
    | AgencySubscriptionType;

  if (plan === "agency") {
    currentSubscription = agencyPlan?.subscription;
  } else if (workspace) {
    currentSubscription =
      plan === "rental"
        ? workspace.rental_subscription
        : workspace.subscription;
  }

  const { pricing: allPricing } = usePricing();
  const { paymentSource: primaryPaymentSource } = usePaymentSource(
    currentSubscription?.id,
  );

  const pricingOptions = allPricing
    .filter(({ id, subscription_type, period, period_unit }) => {
      if (
        currentSubscription?.item_price.period_unit === period_unit &&
        currentSubscription?.item_price.period === period
      ) {
        return currentSubscription.item_price.id === id;
      }
      return subscription_type === (plan === "agency" ? "agency" : "rental");
    })
    .reduce(
      (accumulator, currentItem) => {
        if (currentItem.item_type === "plan") {
          const addon = allPricing.find(
            ({ item_type, period_unit, period, subscription_type }) =>
              item_type === "addon" &&
              period_unit === currentItem.period_unit &&
              period === currentItem.period &&
              subscription_type === currentItem.subscription_type,
          );
          if (addon) {
            accumulator.push({
              plan: currentItem,
              addon,
            });
          }
        }
        return accumulator;
      },
      [] as { addon: Pricing; plan: Pricing }[],
    );

  const visiblePricingOptions = pricingOptions
    .filter(
      ({ plan }) =>
        plan.visible || currentSubscription?.item_price.id === plan.id,
    )
    .sort((option1, option2) => {
      const days1 =
        (option1.plan.period_unit === "month" ? 30 : 360) * option1.plan.period;
      const days2 =
        (option2.plan.period_unit === "month" ? 30 : 360) * option2.plan.period;
      return days1 - days2;
    });

  const [paymentSource, setPaymentSource] = useState(undefined);
  const [currentStep, setCurrentStep] =
    useState<keyof typeof stepLabels>("manage");
  const [pricingPeriodInfo, setPricingPeriodInfo] = useState<PricingPeriodInfo>(
    () => {
      if (fixedPricingPeriodInfo) {
        return fixedPricingPeriodInfo;
      }
      if (currentSubscription) {
        const { period_unit, period } = currentSubscription.item_price;
        return { period, period_unit };
      }
      return {
        period: visiblePricingOptions[0].plan.period,
        period_unit: visiblePricingOptions[0].plan.period_unit,
      };
    },
  );

  useEffect(() => {
    if (primaryPaymentSource && !paymentSource) {
      setPaymentSource(primaryPaymentSource);
    }
  }, [primaryPaymentSource, paymentSource]);

  const pricing = pricingOptions.find(
    ({ plan }) =>
      plan.period === pricingPeriodInfo.period &&
      plan.period_unit === pricingPeriodInfo.period_unit,
  ) || { plan: undefined, addon: undefined };

  let buttonText = "Next";

  if (currentStep === "summary") {
    if (currentSubscription) {
      if (currentSubscription.status === "cancelled") {
        buttonText = "Pay and subscribe";
      } else {
        buttonText = "Update subscription";
      }
    } else {
      buttonText = "Create subscription";
    }
    if (!paymentSource) {
      buttonText = "Add payment method";
    }
  }

  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Title = isDesktop ? DialogTitle : DrawerTitle;

  const MainComponent =
    plan === "agency" ? AgencySubscription : RentalSubscription;

  return (
    <div className="lg:w-[400px] flex flex-col">
      {stepLabels[currentStep] && (
        <Title className="mb-8">{stepLabels[currentStep]}</Title>
      )}
      <RenderIf condition={currentStep === "manage" && !fixedPricingPeriodInfo}>
        <PricingPeriodInfoSelector
          pricingOptions={visiblePricingOptions}
          value={pricingPeriodInfo}
          setValue={setPricingPeriodInfo}
        />
      </RenderIf>
      <RenderIf
        condition={currentStep !== "success" && currentStep !== "payment"}
      >
        <SubscriptionPlan plan={plan} pricingPeriodInfo={pricingPeriodInfo} />
      </RenderIf>
      <MainComponent
        pricing={pricing}
        currentStep={currentStep}
        onSuccess={() => setCurrentStep("success")}
        minQuantity={minQuantity}
      >
        {({
          isNextStepDisabled,
          isNextButtonLoading,
          mainContent,
          onCreateSubscription,
        }) => {
          if (currentStep === "payment") {
            return (
              <PaymentMethod
                subscriptionId=""
                paymentSource={paymentSource}
                onBack={() => setCurrentStep("summary")}
                onSelect={(paymentSource) => {
                  setPaymentSource(paymentSource);
                  setCurrentStep("summary");
                }}
                onNext={(paymentSource) => {
                  setPaymentSource(paymentSource);
                  setCurrentStep("summary");
                }}
              />
            );
          }
          if (currentStep === "success") {
            return <Success />;
          }
          return (
            <>
              {currentStep === "summary" ? (
                <Summary
                  selectedPaymentSource={paymentSource}
                  onChangePaymentSource={() => setCurrentStep("payment")}
                >
                  {mainContent}
                </Summary>
              ) : (
                mainContent
              )}
              <Footer className="mt-6">
                <Button
                  variant="secondary-black"
                  onClick={() => {
                    if (currentStep === "manage") {
                      onClose();
                    }
                    if (currentStep === "summary") {
                      setCurrentStep("manage");
                    }
                  }}
                >
                  Back
                </Button>
                <Button
                  className="ml-auto"
                  disabled={isNextStepDisabled}
                  isLoading={isNextButtonLoading}
                  onClick={() => {
                    if (currentStep === "manage") {
                      setCurrentStep("summary");
                    } else if (currentStep === "summary") {
                      if (paymentSource) {
                        onCreateSubscription(paymentSource.id);
                      } else {
                        setCurrentStep("payment");
                      }
                    }
                  }}
                >
                  {buttonText}
                </Button>
              </Footer>
            </>
          );
        }}
      </MainComponent>
    </div>
  );
}
